import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, Chip, Typography, ChipProps } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ALPHABETIC_REGEX, KEYBOARD_KEYS } from '@/helpers/constants';
import MandatoryLabel from '../MandatoryLabel/MandatoryLabel';
import TooltipComponent from '../Tooltip/TooltipComponent';
import TabLabelIcon from '../../TabLabel/TabLabelIcon';
import styles from './TypeVersionsChipsInput.module.scss';

type TypeVersionsChipsInputComponent = Omit<Partial<ChipProps>, 'variant'> & {
  value: string[];
  handleChange: (newTV: string[]) => void;
  fullWidth?: boolean;
  maxLength: number;
  isMandatory?: boolean;
  isTable?: boolean;
  variant?: 'standard' | 'outlined' | 'filled';
  placeholder?: string;
};

const useStyles = (isTable: boolean) => makeStyles(() => ({
  root: {
    alignItems: 'flex-start',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '5px',
    padding: isTable ? '10px 0px' : '13px 9px 5px 9px',
    marginTop: isTable ? '0px!important' : undefined,
    paddingBottom: isTable ? '5px' : undefined,
  },
  input: {
    alignSelf: 'center',
    flexGrow: 1,
    minWidth: '30px',
    textOverflow: 'ellipsis',
    padding: isTable ? '0px' : '6.5px 4px 8.5px 4px',
    width: 'auto',
  },
  RootLabel: {
    fontSize: '18px',
  },
  tooltipFreeText: {
    maxWidth: 'unset',
  },
}));

const TypeVersionsChipsInput: React.FC<TypeVersionsChipsInputComponent> = (props) => {
  const { id, value, handleChange, label = '', placeholder = '', fullWidth = false, maxLength = 5, isMandatory = false,
    disabled = false, variant = 'outlined', isTable = false } = props;
  const [inputValue, setInputValue] = useState('');
  const classes = useStyles(isTable)();
  const { t } = useTranslation();
  const inputLabel = isMandatory ? (<>{label} <MandatoryLabel /></>) : label;

  const handleDelete = (deleteTV: string) => {
    handleChange(value.filter((tv) => tv !== deleteTV));
  };

  const handleInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const { value: targetValue } = ev.target;
    const rextAccepted = targetValue ? ALPHABETIC_REGEX.test(targetValue) : true;

    if (targetValue.length <= maxLength && rextAccepted) {
      setInputValue(targetValue.toUpperCase());
    }
  };

  const handleKeyDown = (ev) => {
    if ([KEYBOARD_KEYS.ENTER, KEYBOARD_KEYS.COMMA].includes(ev.key)) {
      const newSelectedItem = [...value];

      // Check for same typeversions
      if (newSelectedItem.indexOf(ev.target.value.trim()) !== -1 || !ev.target.value.replace(/\s/g, '').length) {
        setInputValue('');

        return;
      }
      newSelectedItem.push(ev.target.value.trim());
      setInputValue('');
      handleChange(newSelectedItem);
    } else if (value.length && !inputValue.length && ev.key === KEYBOARD_KEYS.BACKSPACE) {
      handleChange(value.slice(0, value.length - 1));
    }
  };

  const handleOnBlur = () => {
    const newSelectedItem = [...value];

    // Check for same typeversions
    if (newSelectedItem.indexOf(inputValue.trim()) !== -1 || !inputValue.replace(/\s/g, '').length) {
      setInputValue('');

      return;
    }
    newSelectedItem.push(inputValue.trim());
    setInputValue('');
    handleChange(newSelectedItem);
  };

  return (
    <TextField
      id={id}
      disabled={disabled}
      label={(
        <>
          {inputLabel}
          {(!disabled && !isTable) && (
            <TooltipComponent
              id="tooltip-typeversions"
              classes={{ tooltip: classes.tooltipFreeText }}
              arrow
              title={<Typography variant="body2">{t('administration.msSection.typeVersionInputHelp')}</Typography>}
            >
              <span className={styles.LabelIcon}><TabLabelIcon /></span>
            </TooltipComponent>
          )}
        </>
      )}
      value={inputValue}
      InputProps={{
        startAdornment: value.map((item: string) => (
          <Chip
            key={item}
            label={item}
            className={styles.Chip}
            onDelete={!disabled ? () => handleDelete(item) : undefined}
            clickable={!disabled}
            size={isTable ? 'small' : 'medium'}
          />
        )),
        onChange: handleInputChange,
        onKeyDown: handleKeyDown,
        onBlur: handleOnBlur,
        placeholder,
        classes: { root: classes.root, input: classes.input },
      }}
      InputLabelProps={{ classes: { root: classes.RootLabel } }}
      fullWidth={fullWidth}
      variant={variant}
    />
  );
};

export default TypeVersionsChipsInput;
